/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file orderHistorySlim.js
 * @description
 * Pure helpers that strip the embedded cashier out of `order.history[].extra.user`.
 * Until backend d0fb04fa, createOrder/payOrder/stornoOrder stored the acting
 * user with its whole `restaurants[]` there — 3.3 MB per paid order at a
 * multi-store tenant. Kept in memory, written to AsyncStorage ('orders',
 * 'lastOrderResponse') and re-read, those rows OOM-killed 128 MB Android POS
 * terminals (Sentry HAPPY-APP-1F / HAPPY-APP-1N). Readers only use
 * `extra.user._id` and `extra.user.name`, so identity fields are kept.
 */

const HISTORY_USER_FIELDS = ['_id', 'name', 'email'];

/**
 * Reduce a history user ref to identity fields. Ids/strings pass through.
 * @param {*} user `history[].extra.user` value.
 * @returns {*} `{_id, name, email}` subset, or the value unchanged.
 */
export const slimHistoryUser = (user) => {
    if (!user || typeof user !== 'object') {
        return user;
    }

    const out = {};

    HISTORY_USER_FIELDS.forEach((key) => {
        if (user[key] !== undefined) {
            out[key] = user[key];
        }
    });

    return out;
};

/**
 * Slim one history entry's `extra.user`. Never mutates the input.
 * @param {*} entry `order.history[]` entry.
 * @returns {*} Entry with a slim `extra.user`, or the entry unchanged.
 */
export const slimHistoryEntry = (entry) => {
    if (!entry || typeof entry !== 'object' || !entry.extra || typeof entry.extra !== 'object'
        || !entry.extra.user || typeof entry.extra.user !== 'object') {
        return entry;
    }

    return {
        ...entry,
        extra: {
            ...entry.extra,
            user: slimHistoryUser(entry.extra.user),
        },
    };
};

/**
 * Slim every history entry of an order (and of `{order}` wrapped responses).
 * Never mutates the input; non-objects pass through.
 * @param {*} order Order (or `{order: …}` API response).
 * @returns {*} Order with slim history entries.
 */
export const slimOrderHistory = (order) => {
    if (!order || typeof order !== 'object' || Array.isArray(order)) {
        return order;
    }

    let next = order;

    if (Array.isArray(order.history)) {
        next = { ...next, history: order.history.map(slimHistoryEntry) };
    }
    if (order.order && typeof order.order === 'object' && Array.isArray(order.order.history)) {
        next = { ...next, order: slimOrderHistory(order.order) };
    }

    return next;
};

/**
 * JSON for the `lastOrderResponse` AsyncStorage key, history slimmed so the
 * row stays far below Android's 2 MB SQLite CursorWindow.
 * @param {*} order Order / API response to persist.
 * @returns {string} Serialized value.
 */
export const serializeLastOrderResponse = (order) => JSON.stringify(slimOrderHistory(order));
